import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './HomeNutritionSections.css';
import productsData from '../data/products.json';
import { optimizeImage } from '../utils/imageOptimizer';



const ProductCard = ({ product }) => {
    const navigate = useNavigate();
    return (
        <div className="nutrition-card1" onClick={() => navigate(`/${product.type}-products/${product.id}`)}>
            <div className="card-img-wrapper">
                <img 
                    src={optimizeImage(product.img, { width: 300, height: 300 })} 
                    alt={product.name} 
                    className="card-img" 
                    loading="lazy"
                    width="300"
                    height="300"
                />
                {product.isComingSoon && (
                    <div className="coming-soon-label">Coming Soon</div>
                )}
                <div className="enquiry-hover-btn">
                    <span>View Details</span>
                </div>
            </div>
            <div className="card-info">
                <h3 className="card-title">{product.name}</h3>
                {!product.isComingSoon && <p className="card-price">{product.price}</p>}
            </div>
        </div>
    );
};


const HomeNutritionSections = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [activeFeatureIdx, setActiveFeatureIdx] = useState(0);

    const features = [
        {
            title: "Quality Nutrition",
            desc: "Our recipes are thoughtfully created to support your pet’s daily nutritional needs, including energy, growth, digestion, skin, coat, and immunity.",
            icon: "https://caninecrown.ascentbw.com/Image/Home/Bone.webp",
            label: "Quality Nutrition"
        },
        {
            title: "Made for Dogs & Cats",
            desc: "We offer dedicated product ranges for both dogs and cats, including age-specific and need-based nutrition.",
            icon: "https://caninecrown.ascentbw.com/Image/Home/Paw.webp",
            label: "Made for Dogs & Cats"
        },
        {
            title: "Trusted by Pet Families",
            desc: "Canine Crown is built with care, responsibility, and a commitment to pet wellness.",
            icon: (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#931A1D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="feature-icon-img" style={{ width: '40px', height: '40px', objectFit: 'contain' }}>
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
            ),
            label: "Trusted by Pet Families"
        },
        {
            title: "Wide Product Range",
            desc: "From dry food and gravy to toppings, biscuits, treats, and shampoos, we provide complete pet care solutions under one trusted brand.",
            icon: "https://caninecrown.ascentbw.com/Image/Home/Rin.webp",
            label: "Wide Product Range"
        },
        {
            title: "Retail & Distribution Support",
            desc: "We work closely with pet shops, retailers, distributors, breeders, and pet care professionals to make quality products accessible.",
            icon: "https://caninecrown.ascentbw.com/Image/Home/Shield.webp",
            label: "Retail & Distribution Support"
        }
    ];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 992);
        };
        handleResize(); // Initial check
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Filter some products for the sections
    const selectedIds = ['dog-1', 'dog-9', 'dog-27', 'dog-30', 'dog-38', 'cat-1', 'cat-9', 'dog-45'];
    const exploreProducts = selectedIds.map(id => productsData.find(p => p.id === id)).filter(Boolean);


    const latestProducts = productsData.filter(p => ['dog-46', 'dog-47', 'dog-48'].includes(p.id));

    /* const nextLatest = () => {
        setLatestIdx((prev) => (prev + 3) % latestProducts.length);
    };

    const prevLatest = () => {
        setLatestIdx((prev) => (prev - 3 + latestProducts.length) % latestProducts.length);
    }; */

    const puppyBg = isMobile 
        ? 'https://caninecrown.ascentbw.com/Image/Home/HomeMob.webp'
        : 'https://caninecrown.ascentbw.com/Image/Home/Home.webp';

    return (
        <div className="home-nutrition-container">
            {/* Section 1: Explore Nutrition */}
            <section className="explore-nutrition-section">
                <div className="section-container">
                    <div className="section-header">
                        <h2 className="hn-title">Explore Nutrition For Your Pet</h2>
                        <Link to="/products" className="view-all-link">VIEW ALL PRODUCTS</Link>
                    </div>
                    <div className="nutrition-grid">
                        {exploreProducts.map(product => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Section 2: Puppy Growth Formula */}
            <section className="puppy-formula-section">
                <div className="puppy-split">
                    {/* Left: Fixed background image */}
                    <div 
                        className="puppy-image-box"
                        style={{ backgroundImage: `url(${optimizeImage(puppyBg, { width: isMobile ? 768 : 1200 })})` }}
                    ></div>

                    {/* Right: Content */}
                    <div className="puppy-content-box">
                        <h2 className="puppy-title">Why Choose Canine Crown?</h2>
                        

                        {/* Selected feature description */}
                        <div className="active-feature-info" style={{ minHeight: '85px', margin: '-10px 0 10px 0' }}>
                            <strong style={{ display: 'block', color: '#931A1D', fontSize: '1.05rem', marginBottom: '6px', fontWeight: '600' }}>
                                {features[activeFeatureIdx].title}
                            </strong>
                            <p style={{ fontSize: '0.92rem', color: '#555', lineHeight: '1.55', margin: 0, fontFamily: "'Inter', sans-serif" }}>
                                {features[activeFeatureIdx].desc}
                            </p>
                        </div>

                        <Link to="/products" className="explore-all-link">EXPLORE ALL NUTRITION FOOD</Link>

                        <div className="formula-features-grid">
                            {features.map((feat, idx) => (
                                <div 
                                    key={idx} 
                                    className={`feature-item ${activeFeatureIdx === idx ? 'active' : ''}`}
                                    onClick={() => setActiveFeatureIdx(idx)}
                                    onMouseEnter={() => setActiveFeatureIdx(idx)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div 
                                        className="feature-icon-box"
                                        style={{
                                            borderColor: activeFeatureIdx === idx ? '#931A1D' : '#e2e2e2',
                                            backgroundColor: activeFeatureIdx === idx ? '#fdf0f0' : 'transparent',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        {typeof feat.icon === 'string' ? (
                                            <img 
                                                src={optimizeImage(feat.icon, { width: 80, height: 80 })} 
                                                alt={feat.title} 
                                                className="feature-icon-img" 
                                                loading="lazy"
                                                width="40"
                                                height="40"
                                            />
                                        ) : (
                                            feat.icon
                                        )}
                                    </div>
                                    <p style={{ 
                                        fontWeight: activeFeatureIdx === idx ? '500' : '400', 
                                        color: activeFeatureIdx === idx ? '#931A1D' : '#333', 
                                        transition: 'color 0.3s ease',
                                        fontSize: '0.82rem',
                                        lineHeight: '1.3'
                                    }}>
                                        {feat.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 3: Latest Nutrition Formulas */}
            <section className="latest-formulas-section">
                <div className="section-container">
                    <div className="section-header center-header">
                        <h2 className="hn-title">Coming Soon: Advanced Nutrition Range</h2>
                        {/* <div className="slider-nav">
                            <button onClick={prevLatest} className="nav-btn">PREVIOUS</button>
                            <span className="nav-sep">/</span>
                            <button onClick={nextLatest} className="nav-btn">NEXT</button>
                        </div> */}
                    </div>
                    <div className="nutrition-grid latest-grid">
                        {latestProducts.map(product => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomeNutritionSections;
